import React, { useEffect, useState } from 'react';
import { noteSignupIntent } from '../utils/signupIntent';
import { usePageMeta } from '../utils/pageMeta';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Box } from '@mui/material';

export default function AdultTraining() {
  usePageMeta('Adult Beach Volleyball Training | {club}', 'Adult beach volleyball training sessions in Orlando, FL. Competitive and recreational options at {facility}.');
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({ text: '', images: [] });
  const [isAdmin, setIsAdmin] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ text: '', images: [] });
  const [savingContent, setSavingContent] = useState(false);

  // Someone browsing Adult Training before signing up gets the adult survey with
  // this option already ticked (utils/signupIntent).
  useEffect(() => { noteSignupIntent('training'); }, []);

  useEffect(() => {
    document.title = 'Adult Training · Torch Beach Volleyball';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap';
    document.head.appendChild(link);
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, 'plans'), where('active', '==', true)));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPlans(all.filter(p => p.athleteType === 'adult' || p.athleteType === 'both'));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();

    getDoc(doc(db, 'settings', 'trainingAdult')).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setContent({ text: d.text || '', images: d.images || [] });
      }
    }).catch(e => console.error(e));

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setIsAdmin(false); return; }
      try {
        const userDoc = await getDoc(doc(db, 'users', u.uid));
        const roles = userDoc.exists() ? (userDoc.data().roles || []) : [];
        setIsAdmin(roles.includes('admin'));
      } catch (e) { console.error(e); }
    });
    return unsub;
  }, []);

  const GROUP_ORDER = ['Single Sessions', 'Session Packs', 'Monthly Memberships', 'Other'];
  const groups = [...new Set(plans.map(p => p.planGroup || 'Other'))]
    .sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a);
      const bi = GROUP_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

  function handleGetStarted(plan) {
    // Quick-checkout plans send visitors straight to mini-reg; signed-in
    // members keep the normal flow (their account already exists).
    if (plan.allowMiniReg === true && plan.planType !== 'recurring' && !auth.currentUser) {
      navigate(`/quick-checkout?plan=${plan.id}`);
      return;
    }
    if (plan.slug) {
      navigate(`/p/${plan.slug}`);
    } else {
      sessionStorage.setItem('selectedPlanId', plan.id);
      if (auth.currentUser) {
        navigate('/training-plans');
      } else {
        sessionStorage.setItem('postRegReturnTo', '/training-plans');
        navigate('/login');
      }
    }
  }

  function startEdit() { setDraft({ text: content.text, images: [...(content.images || [])] }); setEditing(true); }
  function cancelEdit() { setEditing(false); }

  async function saveContent() {
    setSavingContent(true);
    try {
      const next = { text: draft.text || '', images: draft.images || [] };
      await setDoc(doc(db, 'settings', 'trainingAdult'), next);
      setContent(next);
      setEditing(false);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSavingContent(false); }
  }

  function handleContentImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 250 * 1024 && !window.confirm(`This image is ${Math.round(file.size/1024)}KB. Large images bloat the page. Continue?`)) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const [hdr, b64] = ev.target.result.split(',');
      const mime = hdr.replace('data:', '').replace(';base64', '');
      setDraft(d => ({ ...d, images: [...(d.images || []), { data: b64, mime }] }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function removeContentImage(idx) {
    setDraft(d => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));
  }

  const gold = '#C9A84C';
  const goldFaint = 'rgba(201,168,76,0.15)';
  const goldMid = 'rgba(201,168,76,0.6)';

  return (
    <Box sx={{
      minHeight: '100vh', background: '#0D1E35', display: 'flex',
      flexDirection: 'column', alignItems: 'center',
      padding: '36px 16px 56px', fontFamily: '"Barlow", sans-serif', color: 'white',
    }}>
      {/* Back */}
      <Box
        onClick={() => navigate('/?dashboard=1')}
        sx={{
          alignSelf: 'flex-start', color: goldMid, fontSize: '0.8rem', fontWeight: 600,
          letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', mb: 3,
          '&:hover': { color: gold },
        }}
      >
        ← Back to Dashboard
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
        <img src="/horzlogo.png" alt="Torch Beach Volleyball"
             style={{ maxWidth: 280, width: '70vw', objectFit: 'contain', marginBottom: 16 }} />
        <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(2rem,5vw,3rem)', letterSpacing: '4px', lineHeight: 1 }}>
          Adult Training
        </Box>
        <Box sx={{ fontSize: '0.85rem', fontWeight: 300, color: 'rgba(255,255,255,0.45)', mt: 1, letterSpacing: '0.5px' }}>
          Competitive and recreational sessions for adult players of all skill levels
        </Box>
        <Box sx={{ width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, mt: 2, mb: 5 }} />
      </Box>

      {/* Editable page content */}
      {(content.text || (content.images && content.images.length > 0) || isAdmin) && (
        <Box sx={{ width: '100%', maxWidth: 860, mb: 5 }}>
          {!editing && (
            <>
              {content.images && content.images.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: content.text ? 3 : 0 }}>
                  {content.images.map((img, i) => (
                    <img key={i} src={`data:${img.mime};base64,${img.data}`} alt=""
                         style={{ width: content.images.length > 1 ? 'calc(50% - 8px)' : '100%', maxWidth: content.images.length > 1 ? 360 : 480, borderRadius: 12, objectFit: 'cover' }} />
                  ))}
                </Box>
              )}
              {content.text && (
                <Box sx={{ whiteSpace: 'pre-line', textAlign: 'center', fontWeight: 300, fontSize: 'clamp(0.92rem,1.7vw,1.06rem)', lineHeight: 1.6, color: 'rgba(255,255,255,0.72)', maxWidth: 640, mx: 'auto' }}>
                  {content.text}
                </Box>
              )}
              {isAdmin && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <button onClick={startEdit} style={{ fontFamily: '"Barlow", sans-serif', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: gold, background: 'transparent', border: '1px solid ' + goldFaint, borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
                    Edit this section
                  </button>
                </Box>
              )}
            </>
          )}
          {editing && (
            <Box sx={{ background: 'rgba(255,255,255,0.04)', border: '1px solid ' + goldFaint, borderRadius: '14px', p: 2.5 }}>
              <textarea
                value={draft.text}
                onChange={e => setDraft(d => ({ ...d, text: e.target.value }))}
                placeholder="Describe this program...  (blank lines = paragraph breaks)"
                rows={5}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: 'white', border: '1px solid ' + goldFaint, borderRadius: 8, padding: 12, fontFamily: '"Barlow", sans-serif', fontSize: '0.95rem', lineHeight: 1.5, resize: 'vertical' }}
              />
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                {(draft.images || []).map((img, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={`data:${img.mime};base64,${img.data}`} alt=""
                         style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid ' + goldFaint }} />
                    <Box onClick={() => removeContentImage(i)} sx={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', background: '#0D1E35', border: '1px solid ' + gold, color: gold, fontSize: 14, lineHeight: '20px', textAlign: 'center', cursor: 'pointer' }}>x</Box>
                  </Box>
                ))}
                {(draft.images || []).length < 2 && (
                  <Box component="label" sx={{ width: 90, height: 90, borderRadius: 2, border: '1px dashed ' + goldMid, color: goldMid, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.7rem' }}>
                    + Image
                    <input type="file" accept="image/*" onChange={handleContentImage} style={{ display: 'none' }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                <button onClick={saveContent} disabled={savingContent} style={{ fontFamily: '"Barlow", sans-serif', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#0D1E35', background: gold, border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', opacity: savingContent ? 0.6 : 1 }}>
                  {savingContent ? 'Saving...' : 'Save'}
                </button>
                <button onClick={cancelEdit} disabled={savingContent} style={{ fontFamily: '"Barlow", sans-serif', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: goldMid, background: 'transparent', border: '1px solid ' + goldFaint, borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Plan groups */}
      {groups.map(group => (
        <Box key={group} sx={{ width: '100%', maxWidth: 860, mb: 5 }}>
          {groups.length > 1 && (
            <Box sx={{
              fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', letterSpacing: '3px',
              color: goldMid, mb: 2, pb: 1, borderBottom: `1px solid ${goldFaint}`,
            }}>
              {group}
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 2 }}>
            {loading && (
              <Box sx={{ gridColumn: '1/-1', textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.25)' }}>
                Loading…
              </Box>
            )}
            {!loading && plans.filter(p => (p.planGroup || 'Other') === group).sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || (a.price ?? 0) - (b.price ?? 0)).map(plan => (
              <Box key={plan.id} onClick={() => handleGetStarted(plan)} sx={{
                background: 'rgba(255,255,255,0.06)', border: `1px solid ${goldFaint}`,
                borderRadius: '14px', p: '24px 20px 20px', display: 'flex', flexDirection: 'column', gap: 1,
                cursor: 'pointer', transition: 'transform 0.22s, border-color 0.22s, box-shadow 0.22s',
                '&:hover': { transform: 'translateY(-4px)', borderColor: 'rgba(201,168,76,0.5)', boxShadow: '0 10px 32px rgba(0,0,0,0.4)' },
              }}>
                <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem', letterSpacing: '2px' }}>
                  {plan.planName}
                </Box>
                <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', color: gold, lineHeight: 1 }}>
                  ${plan.price}{plan.planType === 'recurring' && <span style={{ fontSize: '1rem', color: goldMid }}>/mo</span>}
                </Box>
                {plan.sessionsIncluded && (
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 300, color: 'rgba(255,255,255,0.45)' }}>
                    {plan.sessionsIncluded} sessions included
                  </Box>
                )}
                {plan.description && (
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 300, color: 'rgba(255,255,255,0.4)', lineHeight: 1.45 }}>
                    {plan.description}
                  </Box>
                )}
                {plan.allowMiniReg === true && plan.planType !== 'recurring' && (
                  <Box
                    onClick={(e) => { e.stopPropagation(); navigate(`/quick-checkout?plan=${plan.id}`); }}
                    sx={{
                      mt: 0.5, alignSelf: 'flex-start', fontSize: '0.68rem', fontWeight: 600,
                      letterSpacing: '1px', textTransform: 'uppercase', color: '#0D1E35',
                      backgroundColor: gold, borderRadius: '5px', px: '10px', py: '5px',
                      cursor: 'pointer', '&:hover': { backgroundColor: '#e0be6a' },
                    }}>
                    Quick Checkout — No Account Needed
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {[plan.planType, plan.planType === 'recurring' ? (plan.rollover !== false ? 'Rollover' : 'Reset') : null]
                    .filter(Boolean).map(tag => (
                      <Box key={tag} sx={{
                        fontSize: '0.6rem', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase',
                        color: goldMid, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: '4px', px: '7px', py: '2px',
                      }}>{tag}</Box>
                    ))}
                </Box>
                <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>
                    {plan.sessionsIncluded && plan.planType !== 'recurring'
                      ? `$${(plan.price / plan.sessionsIncluded).toFixed(2)} / session` : ''}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: '"Barlow", sans-serif', fontSize: '0.75rem', fontWeight: 600,
                      letterSpacing: '1.5px', textTransform: 'uppercase', color: '#0D1E35',
                      background: gold, borderRadius: '6px', px: 2, py: 1,
                      transition: 'background 0.2s',
                      '&:hover': { background: '#e0be6a' },
                    }}
                  >
                    Get Started →
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {!loading && plans.length === 0 && (
        <Box sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.9rem' }}>
          No adult plans available at this time. Check back soon!
        </Box>
      )}

      <Box sx={{ mt: 2, fontSize: '0.7rem', color: 'rgba(255,255,255,0.18)', letterSpacing: '2px', textTransform: 'uppercase' }}>
        © {new Date().getFullYear()} Torch Beach Volleyball · Geneva, FL
      </Box>
    </Box>
  );
}
